import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import { ScriptMarkupBreadcrumb, getCloudinarySrcSet, getCloudinaryUrl } from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale } from 'ecommerce/lib/coupon';

import Breadcrumb from '../components/breadcrumb';
import CatalogProductCard from '../components/custom/catalogProductCard';
import DisplaySets from '../components/display/displaySets';
import FitList from '../components/display/fitList';
import RailGuide from '../components/display/railGuide';
import LandingEditorial from '../components/landing/editorial';
import { HERO_IMAGE_SIZES, HeroCollage, HeroFacts } from '../components/landing/hero';
import { Responsive, SectionHead, ctaTracker, productImage } from '../components/landing/shared';
import LandingStory from '../components/landing/story';
import { Head, Layout } from '../components/layout';
import { ProductDetailLink } from '../components/pageLinks';
import PdpIcon from '../components/product/pdpIcon';
import { isPriced } from '../lib/displayPrices.mjs';
import { money } from '../lib/landingPrice';
import SETTINGS from '../settings';

// Display accessories landing page (docs/display-page.md). Explicit page file, so it wins over
// the category route in [...params].js. Title, meta, H1 and the editorial block come from
// content/categories/display.md, prices from the catalog, the rest is design copy.
const CATEGORY_ID = 'display';
const FIT_ID = 'fit';
const ALL_ID = 'all';
const SETS_ID = 'sets';
const track = ctaTracker('display_page');

const HERO_IMAGES = {
  block: productImage('wooden-rails/5'),
  couple: productImage('wood-block/2'),
  tiny: productImage('photobooth-magnet/2'),
};

const HERO_FACTS = [
  { icon: 'leaf', desktop: 'Oak, maple or black', phone: 'Solid wood' },
  { icon: 'zap', desktop: 'Neodymium magnets', phone: 'Strong magnets' },
  { icon: 'ruler', desktop: 'Sized for Parabo prints', phone: 'Sized to fit' },
];

// Rails list the lengths that fit the print, their chip shows those lengths and their price.
const FINE_ART_RAILS = [12, 14, 16, 17, 20, 24];
const ENGINEER_RAILS = [36, 48];

const FIT_ROWS = [
  {
    name: 'Square Prints',
    printId: 'square-prints',
    sizes: ['2.75, 4 and 5.5 in', '7, 10.2 and 14 cm'],
    image: productImage('square-prints/1'),
    accessories: [{ id: 'wood-block' }, { id: 'photo-ropes' }, { id: 'square-envelopes' }],
  },
  {
    name: 'Classic Prints',
    printId: 'classic-prints',
    sizes: ['4x6 and 5x7 in', '10.2 × 15.2 and 12.7 × 17.8 cm'],
    image: productImage('classic-prints/1'),
    accessories: [{ id: 'wood-block' }, { id: 'photo-ropes' }],
  },
  {
    name: 'Fine Art Prints',
    printId: 'artprint',
    sizes: ['11x14 to 20x24 in', '27.9 × 35.6 to 50.8 × 61 cm'],
    image: productImage('artprint/1'),
    accessories: [{ id: 'wooden-rails', lengths: FINE_ART_RAILS }],
  },
  {
    name: 'Engineer Prints',
    printId: 'engineer-prints',
    sizes: ['3x4 ft and 3x3 ft', '91.4 × 121.9 and 91.4 × 91.4 cm'],
    image: productImage('engineer-prints/3'),
    accessories: [{ id: 'wooden-rails', lengths: ENGINEER_RAILS }],
  },
  {
    name: 'Still on your phone?',
    sizes: ['Print straight onto a magnet'],
    icon: 'smartphone',
    accessories: [{ id: 'acrylic-magnets' }, { id: 'photobooth-magnets' }],
  },
];

// Grouped by how the accessory displays a print. The badge names that, the meta line follows the
// page's unit rule (inches first, centimeters second).
const ACCESSORIES = [
  { id: 'wood-block', badge: 'STAND IT', meta: '3.5 in (8.9 cm) solid oak · Holds up to 5 prints' },
  {
    id: 'wooden-rails',
    badge: 'HANG IT',
    meta: '12 to 48 in (30.5 to 121.9 cm) · Oak, maple or black',
  },
  { id: 'photo-ropes', badge: 'HANG IT', meta: '58 in (147.3 cm) white cable · 8 magnets' },
  {
    id: 'acrylic-magnets',
    badge: 'STICK IT',
    meta: '2.5x2.5 in (6.4 × 6.4 cm) · Printed on acrylic',
  },
  {
    id: 'photobooth-magnets',
    badge: 'STICK IT',
    meta: '2.25x8.625 in (5.7 × 21.9 cm) strips · 4 photos each',
  },
  {
    id: 'square-envelopes',
    badge: 'SEND IT',
    meta: '4.25 or 5.75 in (10.8 or 14.6 cm) · Sets of 25',
  },
];

const SETS = [
  {
    bundleId: 'bundle-square-prints-small',
    printId: 'square-prints',
    title: 'Square Prints & Wood Block',
    includes: '20 Square Prints 4 in (10.2 cm) + Wood Photo Block',
    image: productImage('wood-block/2'),
  },
  {
    bundleId: 'bundle-recprints-small',
    printId: 'classic-prints',
    title: 'Classic Prints & Wood Block',
    includes: '20 Classic Prints 4x6 in (10.2 × 15.2 cm) + Wood Photo Block',
    image: productImage('wood-block/3'),
  },
  {
    bundleId: 'bundle-artprint-11x14',
    printId: 'artprint',
    title: 'Fine Art Print & Rails',
    includes: 'Fine Art Print 11x14 in (27.9 × 35.6 cm) + 12 or 14 in (30.5 or 35.6 cm) rails',
    image: productImage('wooden-rails/2'),
  },
  {
    bundleId: 'bundle-engineer-print',
    printId: 'engineer-prints',
    title: 'Engineer Print & Rails',
    includes: 'Engineer Print 3x4 ft (91.4 × 121.9 cm) + 36 or 48 in (91.4 or 121.9 cm) rails',
    image: productImage('wooden-rails/3'),
  },
];

const COUNT_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];

// Wood Poster Rails FAQ (content/products/wooden-rails.md).
const RAIL_ROWS = [
  {
    name: 'Fine Art Print 11x14 in',
    short: 'Fine Art 11x14 in',
    size: [11, 14],
    portrait: 12,
    landscape: 14,
  },
  {
    name: 'Fine Art Print 12x17 in',
    short: 'Fine Art 12x17 in',
    size: [12, 17],
    portrait: 12,
    landscape: 17,
  },
  {
    name: 'Fine Art Print 16x20 in',
    short: 'Fine Art 16x20 in',
    size: [16, 20],
    portrait: 16,
    landscape: 20,
  },
  {
    name: 'Fine Art Print 20x24 in',
    short: 'Fine Art 20x24 in',
    size: [20, 24],
    portrait: 20,
    landscape: 24,
  },
  {
    name: 'Engineer Print 3x4 ft',
    short: 'Engineer 3x4 ft',
    size: [36, 48],
    portrait: 36,
    landscape: 48,
  },
  {
    name: 'Square Engineer Print 3x3 ft',
    short: 'Square Engineer 3x3 ft',
    size: [36, 36],
    portrait: 36,
    landscape: 36,
  },
];

const DisplayPage = ({ content }) => {
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();

  const category = dataContext.getCategory(CATEGORY_ID);
  const breadcrumb = [{ name: 'Home', url: '/' }, { name: category.name }];
  const accessories = ACCESSORIES.map((accessory) => ({
    ...accessory,
    product: dataContext.getProduct(accessory.id),
  })).filter(({ product }) => isPriced(product));
  const cheapest = accessories.length
    ? Math.min(...accessories.map(({ product }) => product.prices[0]))
    : null;
  const accessoryCount =
    cheapest != null &&
    `${COUNT_WORDS[accessories.length] ?? accessories.length} accessories from ${money(cheapest, accessories[0].product.currency)}.`;

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({
          imageKey: HERO_IMAGES.block.imageKey,
          prefix: HERO_IMAGES.block.prefix,
        })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGES.block)}
        heroImageSizes={HERO_IMAGE_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />

      <Layout>
        <div className="lnd dsp">
          <section className="lnd-hero dsp-hero">
            <Container className="lnd-hero-inner">
              <div className="lnd-hero-copy">
                <Breadcrumb items={breadcrumb} />
                <h1 className="lnd-h1">{category.heroTitle ?? category.name}</h1>
                <p className="lnd-lead">
                  <Responsive
                    desktop="Oak stands, magnetic rails, photo ropes and magnets, sized for Parabo prints. Snap a print in, swap it for a new one anytime. No frame needed."
                    phone="Oak stands, magnetic rails, photo ropes and magnets, sized for Parabo prints. Swap a photo anytime."
                  />
                </p>
                <div className="lnd-hero-actions">
                  <a href={`#${FIT_ID}`} className="lnd-btn" onClick={track('find_fit', 'hero')}>
                    Find what fits your print
                    <PdpIcon name="arrowDown" size={16} />
                  </a>
                  <a
                    href={`#${SETS_ID}`}
                    className="lnd-btn lnd-btn--outline"
                    onClick={track('sets', 'hero')}
                  >
                    <PdpIcon name="package" size={16} className="lnd-desk" />
                    Print + display sets
                    <PdpIcon name="package" size={16} className="lnd-phone" />
                  </a>
                </div>
                <HeroFacts facts={HERO_FACTS} />
              </div>

              <HeroCollage
                images={HERO_IMAGES}
                tag={{ to: 'No frame needed', note: ['Snap it in,', 'swap it anytime.'] }}
              />
            </Container>
          </section>

          <FitList id={FIT_ID} rows={FIT_ROWS} track={track} />

          {accessories.length > 0 && (
            <section id={ALL_ID} className="lnd-section dsp-all">
              <Container>
                <SectionHead
                  eyebrow="All accessories"
                  title="Stand it, hang it, stick it, send it"
                  aside={
                    <Responsive
                      desktop={`${accessoryCount} Stands, rails and ropes hold the prints you order separately.`}
                      phone={accessoryCount}
                    />
                  }
                />
                <ul className="dsp-grid catalog-card-list">
                  {accessories.map(({ product, badge, meta }) => (
                    <li key={product.id}>
                      <ProductDetailLink productId={product.id}>
                        <CatalogProductCard
                          product={product}
                          meta={meta}
                          badge={badge}
                          sale={couponSale(previewSummary, product)}
                          ctaLabel="See details"
                          sizes="(min-width: 992px) 384px, (min-width: 576px) 50vw, 104px"
                          onClick={track('accessory', 'all', product.id)}
                        />
                      </ProductDetailLink>
                    </li>
                  ))}
                </ul>
              </Container>
            </section>
          )}

          <DisplaySets id={SETS_ID} sets={SETS} track={track} />

          <RailGuide rows={RAIL_ROWS} image={productImage('wooden-rails/6')} track={track} />

          <LandingStory
            image={productImage('photo-ropes/4')}
            quote="I love that it can be hung anywhere and that I can change up the pictures faster than I can take them."
            author="@entirely.taylor"
            product="the Photo Rope"
          />

          <LandingEditorial
            eyebrow="About display accessories"
            title="Ways to display your photos at home"
            html={content.extraDescription}
          />
        </div>
      </Layout>
    </>
  );
};

DisplayPage.getInitialProps = async () => ({
  content: await contentApi.category(CATEGORY_ID),
});

DisplayPage.propTypes = {
  content: PropTypes.object.isRequired,
};

export default DisplayPage;
